/**
 * Disclosure policy loader and validation
 *
 * Slice 0 / AT-12 (ADR 0005 §"Policy artefact shape (consolidated v3 — fully
 * authoritative)") bumps the schema to v3. The loader rejects v1 (pre-Slice-0
 * `canonical_iab_taxonomy_version` shape) and v2 (`canonical_demographics`
 * array + flat `confidence_threshold` scalar) with structured errors naming
 * the field and the expected version. v3 introduces:
 *
 *  - `allowed_taxonomies` + `taxonomy_uniformity` (Q5: vocabulary registry).
 *  - `confidence_aggregation` + `confidence_encoding` (Q6.tail).
 *  - `canonical_demographics: Record<key, ValueSchema>` (Q3).
 *  - `claim_family_selection` (Q6: per-family eligibility + caps).
 *  - `sensitivity_penalty` (AT-13 / AT-20 placeholder).
 *  - `allowed_reason_codes` (Q6: selection metadata enumeration).
 *  - `openrtb_data_provider_name` (Q9: OpenRTB projection identity).
 *
 * AT-12 establishes the schema *shape*. AT-14 / AT-15 / AT-19 populate the
 * per-key demographic value schemas. AT-13 populates the sensitivity penalty
 * map. AT-16 wires `claim_family_selection` into the wire-builder.
 *
 * @traces §6.2, §6.3, ADR 0004, ADR 0005
 */
const ALLOWED_AGGREGATIONS = new Set(['confidence_aggregation_v1']);
const ALLOWED_ENCODINGS = new Set(['float32']);
const ALLOWED_UNIFORMITY = new Set(['strict_per_grant']);
const ALLOWED_VALUE_KINDS = new Set(['enum', 'regex', 'open']);
function failClosed(message) {
    throw new Error(message);
}
function validateClaimFamily(family, raw) {
    if (!raw || typeof raw !== 'object') {
        failClosed(`claim_family_selection.${family} must be an object`);
    }
    const r = raw;
    if (typeof r.min_confidence !== 'number' || r.min_confidence < 0 || r.min_confidence > 1) {
        failClosed(`claim_family_selection.${family}.min_confidence must be a number in [0, 1]`);
    }
    if (typeof r.max_age_days !== 'number' || r.max_age_days <= 0) {
        failClosed(`claim_family_selection.${family}.max_age_days must be a positive number`);
    }
    if (typeof r.min_evidence_count !== 'number' || r.min_evidence_count < 1) {
        failClosed(`claim_family_selection.${family}.min_evidence_count must be >= 1`);
    }
    if (typeof r.rank_by !== 'string' || !r.rank_by) {
        failClosed(`claim_family_selection.${family}.rank_by must be a non-empty string`);
    }
    if (typeof r.recency_half_life_days !== 'number' || r.recency_half_life_days <= 0) {
        failClosed(`claim_family_selection.${family}.recency_half_life_days must be positive`);
    }
    if (typeof r.max_facts !== 'number' || r.max_facts < 1) {
        failClosed(`claim_family_selection.${family}.max_facts must be >= 1`);
    }
    if (!r.diversity || typeof r.diversity !== 'object') {
        failClosed(`claim_family_selection.${family}.diversity must be an object`);
    }
    const div = r.diversity;
    if (typeof div.max_per_tier2 !== 'number' || div.max_per_tier2 < 1) {
        failClosed(`claim_family_selection.${family}.diversity.max_per_tier2 must be >= 1`);
    }
    if (typeof div.max_per_tier3 !== 'number' || div.max_per_tier3 < 1) {
        failClosed(`claim_family_selection.${family}.diversity.max_per_tier3 must be >= 1`);
    }
    return {
        min_confidence: r.min_confidence,
        max_age_days: r.max_age_days,
        min_evidence_count: r.min_evidence_count,
        rank_by: r.rank_by,
        recency_half_life_days: r.recency_half_life_days,
        max_facts: r.max_facts,
        diversity: {
            max_per_tier2: div.max_per_tier2,
            max_per_tier3: div.max_per_tier3,
        },
    };
}
function validateDemographicValueSchema(key, raw) {
    if (!raw || typeof raw !== 'object') {
        failClosed(`canonical_demographics.${key} must be an object`);
    }
    const r = raw;
    if (typeof r.kind !== 'string' || !ALLOWED_VALUE_KINDS.has(r.kind)) {
        failClosed(`canonical_demographics.${key}.kind must be one of ${Array.from(ALLOWED_VALUE_KINDS).join('|')}`);
    }
    if (r.kind === 'enum') {
        if (!Array.isArray(r.values) || r.values.length === 0) {
            failClosed(`canonical_demographics.${key}.values must be a non-empty array`);
        }
        if (!r.values.every((v) => typeof v === 'string' && v.length > 0)) {
            failClosed(`canonical_demographics.${key}.values must contain non-empty strings`);
        }
        return { kind: 'enum', values: r.values, description: r.description };
    }
    if (r.kind === 'regex') {
        if (typeof r.pattern !== 'string' || !r.pattern) {
            failClosed(`canonical_demographics.${key}.pattern must be a non-empty string`);
        }
        return { kind: 'regex', pattern: r.pattern, description: r.description };
    }
    // 'open'
    if (typeof r.maxLength !== 'number' || r.maxLength <= 0) {
        failClosed(`canonical_demographics.${key}.maxLength must be a positive number`);
    }
    return { kind: 'open', maxLength: r.maxLength, description: r.description };
}
export function loadDisclosurePolicy(json) {
    const p = json;
    // ─── disclosure_policy_version: must be 3.x.y ──────────────────────
    if (typeof p.disclosure_policy_version !== 'string' || !p.disclosure_policy_version) {
        failClosed('Missing or invalid disclosure_policy_version');
    }
    // Reject pre-Slice-0 v1 shape (canonical_iab_taxonomy_version field).
    if ('canonical_iab_taxonomy_version' in p) {
        failClosed('Pre-Slice-0 (v1) disclosure policy detected (canonical_iab_taxonomy_version). ' +
            'Slice 0 / ADR 0004 replaced this field with canonical_iab_taxonomy: ' +
            '{ spec: "audience", version: "1.1", segtax: 4 }. AT-12 / ADR 0005 further ' +
            'requires disclosure_policy_version "3.0.0" with the consolidated v3 schema.');
    }
    const versionMatch = /^(\d+)\.(\d+)\.(\d+)$/.exec(p.disclosure_policy_version);
    if (!versionMatch) {
        failClosed(`Invalid disclosure_policy_version "${p.disclosure_policy_version}" — expected semver (e.g. "3.0.0").`);
    }
    const major = Number(versionMatch[1]);
    if (major < 3) {
        failClosed(`Slice 0 / AT-12 (ADR 0005) bumped disclosure_policy_version to 3.x.y. ` +
            `Got "${p.disclosure_policy_version}". v2 is rejected: it carries ` +
            `canonical_demographics as an array and a flat confidence_threshold scalar; ` +
            `v3 carries canonical_demographics as Record<key, ValueSchema> and ` +
            `claim_family_selection.<family>.min_confidence per ADR 0005.`);
    }
    if (major > 3) {
        failClosed(`disclosure_policy_version "${p.disclosure_policy_version}" is newer than this ` +
            `loader supports (3.x.y). Refusing to silently accept an unknown future shape.`);
    }
    // ─── canonical_iab_taxonomy ────────────────────────────────────────
    const tax = p.canonical_iab_taxonomy;
    if (!tax || typeof tax !== 'object') {
        failClosed('Missing or invalid canonical_iab_taxonomy');
    }
    const taxRecord = tax;
    if (taxRecord.spec !== 'audience') {
        failClosed(`canonical_iab_taxonomy.spec must be "audience" (got: ${JSON.stringify(taxRecord.spec)})`);
    }
    if (taxRecord.version !== '1.1') {
        failClosed(`canonical_iab_taxonomy.version must be "1.1" (got: ${JSON.stringify(taxRecord.version)})`);
    }
    if (taxRecord.segtax !== 4) {
        failClosed(`canonical_iab_taxonomy.segtax must be 4 (got: ${JSON.stringify(taxRecord.segtax)})`);
    }
    // ─── allowed_taxonomies ────────────────────────────────────────────
    if (!Array.isArray(p.allowed_taxonomies) || p.allowed_taxonomies.length === 0) {
        failClosed('Missing or empty allowed_taxonomies (must include at least "iab-audience-1.1")');
    }
    if (!p.allowed_taxonomies.every((t) => typeof t === 'string' && t.length > 0)) {
        failClosed('allowed_taxonomies must contain non-empty strings');
    }
    if (!p.allowed_taxonomies.includes('iab-audience-1.1')) {
        failClosed('allowed_taxonomies must include "iab-audience-1.1" (Slice 0 / ADR 0004 vocabulary)');
    }
    // ─── taxonomy_uniformity ───────────────────────────────────────────
    if (typeof p.taxonomy_uniformity !== 'string' || !ALLOWED_UNIFORMITY.has(p.taxonomy_uniformity)) {
        failClosed(`taxonomy_uniformity must be one of ${Array.from(ALLOWED_UNIFORMITY).join('|')} ` +
            `(got: ${JSON.stringify(p.taxonomy_uniformity)})`);
    }
    // ─── confidence_aggregation ────────────────────────────────────────
    if (typeof p.confidence_aggregation !== 'string' ||
        !ALLOWED_AGGREGATIONS.has(p.confidence_aggregation)) {
        failClosed(`confidence_aggregation must be one of ${Array.from(ALLOWED_AGGREGATIONS).join('|')} ` +
            `(got: ${JSON.stringify(p.confidence_aggregation)})`);
    }
    // ─── confidence_encoding ───────────────────────────────────────────
    if (typeof p.confidence_encoding !== 'string' || !ALLOWED_ENCODINGS.has(p.confidence_encoding)) {
        failClosed(`confidence_encoding must be one of ${Array.from(ALLOWED_ENCODINGS).join('|')} ` +
            `(got: ${JSON.stringify(p.confidence_encoding)})`);
    }
    // ─── canonical_demographics: Record<key, ValueSchema> ──────────────
    if (Array.isArray(p.canonical_demographics)) {
        failClosed('canonical_demographics must be a Record<key, ValueSchema> object (v3 / ADR 0005), ' +
            'not an array. Detected v2 shape — bump to disclosure_policy_version 3.x.y.');
    }
    if (!p.canonical_demographics || typeof p.canonical_demographics !== 'object') {
        failClosed('Missing or invalid canonical_demographics (must be a Record<key, ValueSchema>)');
    }
    const demos = p.canonical_demographics;
    const validatedDemos = {};
    for (const [key, schema] of Object.entries(demos)) {
        if (!key || typeof key !== 'string') {
            failClosed('canonical_demographics keys must be non-empty strings');
        }
        validatedDemos[key] = validateDemographicValueSchema(key, schema);
    }
    // ─── claim_family_selection ────────────────────────────────────────
    if (!p.claim_family_selection || typeof p.claim_family_selection !== 'object') {
        failClosed('Missing or invalid claim_family_selection (must contain interests + purchase_intent)');
    }
    const cfs = p.claim_family_selection;
    if (!('interests' in cfs)) {
        failClosed('claim_family_selection.interests is required');
    }
    if (!('purchase_intent' in cfs)) {
        failClosed('claim_family_selection.purchase_intent is required');
    }
    const interests = validateClaimFamily('interests', cfs.interests);
    const purchaseIntent = validateClaimFamily('purchase_intent', cfs.purchase_intent);
    // ─── sensitivity_penalty ───────────────────────────────────────────
    if (p.sensitivity_penalty === undefined ||
        p.sensitivity_penalty === null ||
        Array.isArray(p.sensitivity_penalty) ||
        typeof p.sensitivity_penalty !== 'object') {
        failClosed('Missing or invalid sensitivity_penalty (must be a Record<string, number>; may be empty)');
    }
    const penalties = {};
    for (const [id, penalty] of Object.entries(p.sensitivity_penalty)) {
        if (typeof penalty !== 'number' || penalty < 0 || penalty > 1) {
            failClosed(`sensitivity_penalty["${id}"] must be a number in [0, 1] (got: ${JSON.stringify(penalty)})`);
        }
        penalties[id] = penalty;
    }
    // ─── allowed_reason_codes ──────────────────────────────────────────
    if (!Array.isArray(p.allowed_reason_codes) || p.allowed_reason_codes.length === 0) {
        failClosed('Missing or empty allowed_reason_codes');
    }
    if (!p.allowed_reason_codes.every((c) => typeof c === 'string' && c.length > 0)) {
        failClosed('allowed_reason_codes must contain non-empty strings');
    }
    // ─── openrtb_data_provider_name ────────────────────────────────────
    if (typeof p.openrtb_data_provider_name !== 'string' || !p.openrtb_data_provider_name) {
        failClosed('Missing or invalid openrtb_data_provider_name');
    }
    // ─── canonical_reputation_levels ───────────────────────────────────
    if (!Array.isArray(p.canonical_reputation_levels) || p.canonical_reputation_levels.length === 0) {
        failClosed('Missing or empty canonical_reputation_levels');
    }
    if (!p.canonical_reputation_levels.every((item) => typeof item === 'string' && item.length > 0)) {
        failClosed('canonical_reputation_levels must contain non-empty strings');
    }
    // ─── flags ─────────────────────────────────────────────────────────
    if (typeof p.demographics_default_enabled !== 'boolean') {
        failClosed('Missing or invalid demographics_default_enabled');
    }
    if (typeof p.reputation_default_enabled !== 'boolean') {
        failClosed('Missing or invalid reputation_default_enabled');
    }
    return {
        disclosure_policy_version: p.disclosure_policy_version,
        canonical_iab_taxonomy: {
            spec: 'audience',
            version: '1.1',
            segtax: 4,
        },
        allowed_taxonomies: p.allowed_taxonomies,
        taxonomy_uniformity: 'strict_per_grant',
        confidence_aggregation: 'confidence_aggregation_v1',
        confidence_encoding: 'float32',
        canonical_demographics: validatedDemos,
        claim_family_selection: {
            interests,
            purchase_intent: purchaseIntent,
        },
        sensitivity_penalty: penalties,
        allowed_reason_codes: p.allowed_reason_codes,
        openrtb_data_provider_name: p.openrtb_data_provider_name,
        canonical_reputation_levels: p.canonical_reputation_levels,
        demographics_default_enabled: p.demographics_default_enabled,
        reputation_default_enabled: p.reputation_default_enabled,
    };
}
